import { Section } from '@/components/Section';
import { FormContainer } from '@/components/form/FormContainer';
import { Dialog } from 'alurkerja-ui';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { IResultSchema, resultSchema, ResultService } from '@/api/result';
import { CreateResultForm } from '@/components/result/CreateResultForm';
import { useBackNavigate } from '@/utils/hooks/useBackNavigate';
import { IDetailTestRunSchema } from '@/api/testRunDetail';

type CreateResultProps = {
  backAction?: () => void;
  testRun: IDetailTestRunSchema;
  onSuccess?: () => void;
  setSaveAction?: (action: () => void) => void;
  setIsSaving?: (isSaving: boolean) => void;
};

export const CreateResult: React.FC<CreateResultProps> = ({
  backAction,
  testRun,
  onSuccess,
  setSaveAction,
  setIsSaving,
}) => {
  const backNavigate = useBackNavigate;
  const toBack = () => (backAction ? backAction() : backNavigate());

  const form = useForm<IResultSchema>({
    resolver: zodResolver(resultSchema),
    values: undefined,
  });

  const { mutate: create, isPending: isCreateLoading } =
    ResultService.useCreate();

  // Sync loading state with parent
  useEffect(() => {
    if (setIsSaving) {
      setIsSaving(isCreateLoading);
    }
  }, [isCreateLoading, setIsSaving]);

  const submitAction = (data: IResultSchema) => {
    data.test_run_detail_id = testRun.id;
    create(data, {
      onSuccess: () => {
        Dialog.success({
          title: 'Success',
          description: 'Result has been created',
          callback: () => {
            if (onSuccess) {
              onSuccess();
            }
            toBack();
          },
        });
      },
      onError: (error: any) => {
        Dialog.error({
          title: 'Error',
          description:
            error?.response?.data?.message ?? 'Failed to create result',
        });
      },
    });
  };

  // Set the save action when the component mounts
  useEffect(() => {
    if (setSaveAction) {
      setSaveAction(() => form.handleSubmit(submitAction));
    }
  }, [setSaveAction, form]);

  return (
    <FormContainer form={form} onSubmit={submitAction}>
      <Section title="Add Result">
        <CreateResultForm />
      </Section>
    </FormContainer>
  );
};
